import axios from 'axios';
import { useToken } from '@/hooks';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import { Logout } from '@/utils/common';
import Cookies from 'js-cookie';
import { decryptData, encryptData } from '@/components/reusable/crypto/Cipher';
/**
 * Sets up the Axios interceptors and returns the Axios instance.
 * @param {function} navigate - The navigation function.
 */

let globalNavigate = null;

export const setGlobalNavigate = (navFn) => {
  globalNavigate = navFn;
};

/**
 * @param {object} data - The response body (error?.response?.data).
 * @returns {string[]} Deduplicated error messages.
 */
const extractErrorMessages = (data) => {
  const collect = (val, out) => {
    if (val === null || val === undefined) return;
    if (typeof val === 'string') {
      out.push(val);
    } else if (Array.isArray(val)) {
      val.forEach((v) => collect(v, out));
    } else if (typeof val === 'object') {
      Object.values(val).forEach((v) => collect(v, out));
    }
  };

  const errorNode = data?.error;

  // salesId takes priority — surface only that when present
  if (errorNode?.salesId) {
    const salesIdMessages = [];
    collect(errorNode.salesId, salesIdMessages);
    if (salesIdMessages.length) return [...new Set(salesIdMessages)];
  }

  const messages = [];
  collect(errorNode, messages);
  const unique = [...new Set(messages)];

  return unique.length ? unique : [data?.response || 'Something went wrong!'];
};
const setUpInterceptor = (navigate, hideToast = false) => {
  const apiService = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL,
  });

  let isRefreshing = false;
  let failedQueue = [];
  let refreshPromise = null;

  const processQueue = (error, token = null) => {
    failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token);
      }
    });
    failedQueue = [];
  };

  // REQUEST INTERCEPTOR
  apiService.interceptors.request.use(
    (config) => {
      const token = useToken();
      const branchCode = Cookies.get('branchCode');
      const companyCode = Cookies.get('companyCode');
      const financialYear = Cookies.get('financialYear');

      const userData = decryptData(localStorage.getItem('userData'));
      const financialYearId = userData?.financialId;

      if (!token) {
        globalNavigate && globalNavigate('/login/');
        return Promise.reject('No token. Redirecting to login.');
      }

      config.headers.Authorization = `Bearer ${token}`;

      if (branchCode) config.headers['Branch'] = branchCode;
      if (companyCode) config.headers['Company'] = companyCode;
      config.headers['FinancialYear'] = financialYear || financialYearId;

      return config;
    },
    (error) => Promise.reject(error),
  );

  // RESPONSE INTERCEPTOR
  apiService.interceptors.response.use(
    (response) => {
      return response?.data;
    },

    async (error) => {
      const originalRequest = error.config;

      // if (hideToast) return;

      // TOKEN EXPIRED CASE
      if (
        error?.response?.status === 401 &&
        error?.response?.data?.ResponseCode === '40101'
      ) {
        // Already refreshing → queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                resolve(apiService(originalRequest));
              },
              reject,
            });
          });
        }
        // Start refresh flow
        isRefreshing = true;

        const refreshToken = decryptData(Cookies.get('refreshToken'));

        refreshPromise = axios.post(
          `${import.meta.env.VITE_APP_API_URL}/auth/refresh/`,
          { refreshToken },
        );

        try {
          const { data } = await refreshPromise;
          const newToken = data.access;
          Cookies.set('token', encryptData(newToken), { expires: 7 });
          processQueue(null, newToken);

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return apiService(originalRequest);
        } catch (error) {
          processQueue(error, null);
          Logout();
          globalNavigate && globalNavigate('/login');
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }

        // return new Promise(async (resolve, reject) => {
        //   try {
        //     const refreshToken = decryptData(Cookies.get('refreshToken'));

        //     const res = await axios.post(
        //       `${import.meta.env.VITE_APP_API_URL}/auth/refresh/`,
        //       { refreshToken },
        //     );

        //     const newToken = res.data.access;

        //     Cookies.set('token', encryptData(newToken), { expires: 7 });

        //     apiService.defaults.headers['Authorization'] = `Bearer ${newToken}`;

        //     processQueue(null, newToken);

        //     originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        //     resolve(apiService(originalRequest));
        //   } catch (err) {
        //     processQueue(err, null);
        //     Logout();
        //     navigate && navigate('/login/');
        //     reject(err);
        //   } finally {
        //     isRefreshing = false;
        //   }
        // });
      }

      // OTHER ERROR HANDLING
      if (error?.response?.status === 401) {
        const errMessage = error.response.data.response;
        showToast(errMessage, 'error');
        sessionStorage.setItem('redirectUrl', window.location.pathname);

        setTimeout(() => {
          Logout();
        }, 2000);
      } else if (error.response?.status === 403) {
        globalNavigate && globalNavigate('/permission/z/');
      } else if (error.response?.status === 400) {
        extractErrorMessages(error?.response?.data).forEach((eachError) =>
          showToast(eachError, 'error'),
        );
      } else {
        const errMessage =
          error?.response?.data?.error?.[0] ?? error?.response?.data?.response;

        showToast(errMessage, 'error');
      }

      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        showToast('The request timed out. Try again later.', 'error');
      } else if (!navigator.onLine) {
        showToast('No Internet Connection.', 'error');
      }

      return Promise.reject(error);
    },
  );

  return apiService;
};

export default setUpInterceptor;
